import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, X, Building2, Search, Lock, Globe } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { getRegionsForCountry } from '../../data/regions';
import IndustryMultiSelectFilter from '../IndustryMultiSelectFilter';
import { projectSectorsOf, MAX_PROJECT_SECTORS } from '../../constants/industries'; // WO-73
import YearPicker from '../YearPicker';
import { planGateFromResponse } from '../../lib/planGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STAKEHOLDER_ROLES = ['Developer', 'Contractor', 'Consultant', 'Supplier', 'Others'];

const countries = [
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'AE', en: 'UAE', ar: 'الإمارات' },
  { code: 'OM', en: 'Oman', ar: 'عمان' },
  { code: 'KW', en: 'Kuwait', ar: 'الكويت' },
  { code: 'BH', en: 'Bahrain', ar: 'البحرين' },
  { code: 'QA', en: 'Qatar', ar: 'قطر' },
  { code: 'EG', en: 'Egypt', ar: 'مصر' },
  { code: 'JO', en: 'Jordan', ar: 'الأردن' },
  { code: 'IQ', en: 'Iraq', ar: 'العراق' },
  { code: 'LB', en: 'Lebanon', ar: 'لبنان' },
  { code: 'YE', en: 'Yemen', ar: 'اليمن' },
  { code: 'MA', en: 'Morocco', ar: 'المغرب' },
  { code: 'TR', en: 'Turkey', ar: 'تركيا' },
  { code: 'PK', en: 'Pakistan', ar: 'باكستان' },
  { code: 'IN', en: 'India', ar: 'الهند' },
];

const stages = [
  { value: 'concept', en: 'Concept', ar: 'مفهوم' },
  { value: 'design', en: 'Design', ar: 'تصميم' },
  { value: 'tender', en: 'Tender', ar: 'مناقصة' },
  { value: 'execution', en: 'Execution', ar: 'تنفيذ' },
  { value: 'complete', en: 'Completed', ar: 'مكتمل' }
];

const stageProgress = { concept: 10, design: 30, tender: 50, execution: 75, complete: 100 };

export function AdminProjectDialog({ open, onOpenChange, project, token, language, onSuccess, userPlan = 'free', onPlanGate }) {
  const isEdit = !!project;
  const isRTL = language === 'ar';
  const canSetPrivate = ['premium', 'enterprise'].includes(userPlan);

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [newStakeholderRole, setNewStakeholderRole] = useState('Developer');
  const [newStakeholderName, setNewStakeholderName] = useState('');

  const emptyForm = {
    canonical_name: '',
    country: 'SA',
    city: '',
    region: '',
    sectors: [],
    stage: 'concept',
    value_min: '',
    value_max: '',
    currency: 'USD',
    description: '',
    location_url: '',
    linked_company_ids: [],
    stakeholders: [],
    progress: stageProgress['concept'],
    is_master_project: false,
    visibility: 'public',
    start_date: '',
    end_date: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  // Fetch companies
  useEffect(() => {
    if (!open) return;
    setLoadingCompanies(true);
    fetch(`${BACKEND_URL}/api/intelligence/companies?page_size=100`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => setCompanies(data.items || data.data || []))
      .catch(() => {})
      .finally(() => setLoadingCompanies(false));
  }, [open, token]);

  // Populate form when editing
  useEffect(() => {
    if (project) {
      setFormData({
        canonical_name: project.canonical_name || '',
        country: project.country || 'SA',
        city: project.city || '',
        region: project.region || '',
        sectors: projectSectorsOf(project),
        stage: project.stage || 'concept',
        value_min: (project.value_estimate?.min != null) ? String(project.value_estimate.min) : '',
        value_max: (project.value_estimate?.max != null) ? String(project.value_estimate.max) : '',
        currency: 'USD',
        description: project.description || '',
        location_url: project.location_url || '',
        linked_company_ids: project.linked_company_ids || [],
        stakeholders: project.stakeholders ? [...project.stakeholders] : [],
        progress: project.progress ?? stageProgress[project.stage] ?? 0,
        is_master_project: project.is_master_project || false,
        visibility: project.visibility || 'public',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
      });
    } else {
      setFormData(emptyForm);
    }
    setCompanySearch('');
    setNewStakeholderName('');
    setNewStakeholderRole('Developer');
  }, [project, open]);

  // Auto-update progress when stage changes (only if user hasn't manually set it)
  const handleStageChange = (value) => {
    setFormData(prev => ({
      ...prev,
      stage: value,
      progress: stageProgress[value] ?? prev.progress,
    }));
  };

  const handleAddStakeholder = () => {
    if (!newStakeholderName.trim()) return;
    setFormData(prev => ({
      ...prev,
      stakeholders: [...prev.stakeholders, { role: newStakeholderRole, company_id: newStakeholderName.trim() }]
    }));
    setNewStakeholderName('');
  };

  const toggleCompany = (companyId) => {
    setFormData(prev => ({
      ...prev,
      linked_company_ids: prev.linked_company_ids.includes(companyId)
        ? prev.linked_company_ids.filter(id => id !== companyId)
        : [...prev.linked_company_ids, companyId]
    }));
  };

  const filteredCompanies = companies.filter(c =>
    c.canonical_name?.toLowerCase().includes(companySearch.toLowerCase())
  );

  const wordCount = formData.description?.trim()
    ? formData.description.trim().split(/\s+/).length
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.canonical_name.trim()) {
      toast.error(language === 'ar' ? 'اسم المشروع مطلوب' : 'Project name is required');
      return;
    }
    setLoading(true);
    try {
      const url = isEdit
        ? `${BACKEND_URL}/api/admin/projects/${project._id}`
        : `${BACKEND_URL}/api/admin/projects`;
      const payload = {
        ...formData,
        value_min: formData.value_min ? parseFloat(formData.value_min) : null,
        value_max: formData.value_max ? parseFloat(formData.value_max) : null,
        visibility: canSetPrivate ? formData.visibility : 'public',
      };
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (pg && onPlanGate) { onOpenChange(false); onPlanGate(pg); return; }
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save project');
      }
      toast.success(isEdit
        ? (language === 'ar' ? 'تم تحديث المشروع بنجاح' : 'Project updated successfully')
        : (language === 'ar' ? 'تم إنشاء المشروع بنجاح' : 'Project created successfully')
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const t = (en, ar) => language === 'ar' ? ar : en;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {t(isEdit ? 'Edit Project' : 'Create Project', isEdit ? 'تعديل المشروع' : 'إنشاء مشروع جديد')}
          </DialogTitle>
          <DialogDescription>{t('Enter project details', 'أدخل تفاصيل المشروع')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Project Name */}
          <div className="space-y-2">
            <Label>{t('Project Name', 'اسم المشروع')} *</Label>
            <Input
              value={formData.canonical_name}
              onChange={(e) => setFormData(prev => ({ ...prev, canonical_name: e.target.value }))}
              placeholder={t('Enter project name...', 'أدخل اسم المشروع...')}
              className="bg-background"
              data-testid="project-name-input"
            />
          </div>

          {/* Country + Region */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Country', 'الدولة')}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => setFormData(prev => ({ ...prev, country: value, region: '' }))}
              >
                <SelectTrigger data-testid="project-country-select" className="bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{isRTL ? c.ar : c.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Region / Province', 'المنطقة / الإمارة')}</Label>
              {getRegionsForCountry(formData.country) ? (
                <Select
                  value={formData.region || ''}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}
                >
                  <SelectTrigger data-testid="project-region-select" className="bg-background">
                    <SelectValue placeholder={t('Select region...', 'اختر المنطقة...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getRegionsForCountry(formData.country).map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.region || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                  placeholder={t('Region / Province', 'المنطقة / الإقليم')}
                  data-testid="project-region-input"
                />
              )}
            </div>
          </div>

          {/* City + Location URL */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('City', 'المدينة')}</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Riyadh, Dubai..."
                className="bg-background"
                data-testid="project-city-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('Location URL (Google Maps)', 'رابط الموقع')}</Label>
              <Input
                value={formData.location_url}
                onChange={(e) => setFormData(prev => ({ ...prev, location_url: e.target.value }))}
                placeholder="https://maps.google.com/..."
                className="bg-background"
                data-testid="project-location-url-input"
              />
            </div>
          </div>

          {/* Industry + Stage */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Industry', 'الصناعة')}</Label>
              <IndustryMultiSelectFilter
                selected={formData.sectors || []}
                onChange={(vals) => setFormData(prev => ({ ...prev, sectors: vals }))}
                language={language}
                triggerClassName="w-full bg-background"
                popoverAlign="start"
                scope="projects"
                max={MAX_PROJECT_SECTORS}
                data-testid="project-sectors-select"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('Stage', 'المرحلة')}</Label>
              <Select value={formData.stage} onValueChange={handleStageChange}>
                <SelectTrigger data-testid="project-stage-select" className="bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => (
                    <SelectItem key={s.value} value={s.value}>{isRTL ? s.ar : s.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Start Date + End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                {t('Start Date', 'تاريخ البدء')}
                <span className="text-xs font-normal text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                  {t('Tentative', 'تقديري')}
                </span>
              </Label>
              <YearPicker
                value={formData.start_date || ''}
                onChange={(v) => setFormData(prev => ({ ...prev, start_date: v }))}
                language={language}
                placeholder={t('Select start year...', 'اختر سنة البدء...')}
                className="bg-background"
                data-testid="project-start-date-picker"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                {t('End Date', 'تاريخ الانتهاء')}
                <span className="text-xs font-normal text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                  {t('Tentative', 'تقديري')}
                </span>
              </Label>
              <YearPicker
                value={formData.end_date || ''}
                onChange={(v) => setFormData(prev => ({ ...prev, end_date: v }))}
                language={language}
                placeholder={t('Select end year...', 'اختر سنة الانتهاء...')}
                className="bg-background"
                data-testid="project-end-date-picker"
              />
            </div>
          </div>

          {/* Estimated Value */}
          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              <span>{t('Estimated Value', 'القيمة التقديرية')}</span>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {t('Values in millions of USD (Mill. USD)', 'القيم بملايين الدولارات الأمريكية (مليون USD)')}
              </span>
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('Min (Mill. USD)', 'الحد الأدنى (مليون USD)')}</Label>
                <Input
                  type="number"
                  value={formData.value_min}
                  onChange={(e) => setFormData(prev => ({ ...prev, value_min: e.target.value }))}
                  placeholder="e.g. 1"
                  className="bg-background"
                  data-testid="project-value-min-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('Max (Mill. USD)', 'الحد الأقصى (مليون USD)')}</Label>
                <Input
                  type="number"
                  value={formData.value_max}
                  onChange={(e) => setFormData(prev => ({ ...prev, value_max: e.target.value }))}
                  placeholder="e.g. 5"
                  className="bg-background"
                  data-testid="project-value-max-input"
                />
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              <span>{t('Progress', 'التقدم')}</span>
              <span className="font-bold text-primary">{formData.progress}%</span>
            </Label>
            <input
              type="range"
              min={0} max={100} step={5}
              value={formData.progress}
              onChange={(e) => setFormData(prev => ({ ...prev, progress: Number(e.target.value) }))}
              className="w-full accent-primary cursor-pointer"
              data-testid="project-progress-slider"
            />
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2 transition-all"
                style={{ width: `${formData.progress}%` }}
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t('Description', 'الوصف')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => {
                const text = e.target.value;
                const wc = text.trim() ? text.trim().split(/\s+/).length : 0;
                if (wc <= 500) setFormData(prev => ({ ...prev, description: text }));
              }}
              placeholder={t('Brief project description (up to 500 words)...', 'وصف موجز للمشروع (حتى 500 كلمة)...')}
              className="min-h-[100px] resize-y bg-background"
              data-testid="project-description-input"
            />
            <p className={`text-xs text-right ${wordCount > 450 ? 'text-orange-500 font-medium' : 'text-muted-foreground'}`}>
              {wordCount} / 500 {t('words', 'كلمة')}
            </p>
          </div>

          {/* Stakeholders */}
          <div className="space-y-3 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t('Stakeholders', 'أصحاب المصلحة')}
            </Label>
            {/* Existing */}
            <div className="flex flex-wrap gap-2">
              {formData.stakeholders.map((s, idx) => (
                <Badge key={idx} variant="outline" className="capitalize flex items-center gap-1 pr-1">
                  {s.role}: {s.company_id}
                  <button
                    type="button"
                    className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                    onClick={() => setFormData(prev => ({ ...prev, stakeholders: prev.stakeholders.filter((_, i) => i !== idx) }))}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {formData.stakeholders.length === 0 && (
                <p className="text-xs text-muted-foreground">{t('No stakeholders added yet', 'لم يتم إضافة أصحاب مصلحة بعد')}</p>
              )}
            </div>
            {/* Add new */}
            <div className="flex gap-2 items-end">
              <div className="space-y-1 w-36">
                <Label className="text-xs text-muted-foreground">{t('Role', 'الدور')}</Label>
                <Select value={newStakeholderRole} onValueChange={setNewStakeholderRole}>
                  <SelectTrigger className="h-8 text-xs bg-background" data-testid="new-stakeholder-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAKEHOLDER_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-muted-foreground">{t('Company / Entity', 'الشركة / الجهة')}</Label>
                <Input
                  className="h-8 text-xs bg-background"
                  value={newStakeholderName}
                  onChange={(e) => setNewStakeholderName(e.target.value)}
                  placeholder={t('Company name...', 'اسم الشركة...')}
                  data-testid="new-stakeholder-name"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddStakeholder(); } }}
                />
              </div>
              <Button
                type="button" size="sm" variant="outline" className="h-8 shrink-0"
                disabled={!newStakeholderName.trim()} onClick={handleAddStakeholder}
                data-testid="add-stakeholder-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('Add', 'إضافة')}
              </Button>
            </div>
          </div>

          {/* Linked Companies */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t('Linked Companies', 'الشركات المرتبطة')}
            </Label>
            {formData.linked_company_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.linked_company_ids.map(cid => {
                  const co = companies.find(c => c._id === cid);
                  return co ? (
                    <Badge key={cid} variant="default" className="gap-1 bg-purple-500/20 text-purple-700 border-purple-300">
                      {co.canonical_name}
                      <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => toggleCompany(cid)} />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder={t('Search companies...', 'ابحث عن شركات...')}
                className="pl-9 bg-background"
              />
            </div>
            <div className="border rounded-md max-h-40 overflow-y-auto">
              {companySearch.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">{t('Type to search companies...', 'اكتب للبحث عن شركات...')}</div>
              ) : loadingCompanies ? (
                <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : filteredCompanies.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">{t('No companies found', 'لا توجد شركات')}</div>
              ) : (
                filteredCompanies.slice(0, 20).map(company => (
                  <div
                    key={company._id}
                    className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                    onClick={() => toggleCompany(company._id)}
                  >
                    <Checkbox checked={formData.linked_company_ids.includes(company._id)} onCheckedChange={() => toggleCompany(company._id)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{company.canonical_name}</div>
                      {company.country && <div className="text-xs text-muted-foreground">{company.country}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Master Project Toggle */}
          <div className="space-y-2 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  {t('Master Project', 'مشروع رئيسي')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('Mark this project as a Master Project that groups sub-projects under it',
                     'تفعيل هذا الخيار يجعل المشروع مشروعاً رئيسياً يضم مشاريع فرعية')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {formData.is_master_project ? t('Yes', 'نعم') : t('No', 'لا')}
                </span>
                <Switch
                  checked={formData.is_master_project}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_master_project: checked }))}
                  data-testid="project-master-toggle"
                />
              </div>
            </div>
          </div>

          {/* Visibility (premium only) */}
          {canSetPrivate && (
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    {formData.visibility === 'private'
                      ? <Lock className="w-4 h-4 text-amber-500" />
                      : <Globe className="w-4 h-4 text-emerald-500" />}
                    {t('Visibility', 'الرؤية')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.visibility === 'private'
                      ? t('Private — only people with the link can access', 'خاص — فقط من لديهم الرابط')
                      : t('Public — visible to everyone', 'عام — مرئي للجميع')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {formData.visibility === 'private' ? t('Private', 'خاص') : t('Public', 'عام')}
                  </span>
                  <Switch
                    checked={formData.visibility === 'private'}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, visibility: checked ? 'private' : 'public' }))}
                    data-testid="project-visibility-toggle"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="project-save-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? t('Saving...', 'جارٍ الحفظ...') : t('Save', 'حفظ')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminProjectDialog;
